import React from 'react';

const TRAVEL_STYLES = [
  { id: 'beach', label: 'Beach House', emoji: '\uD83C\uDFD6\uFE0F' },
  { id: 'mountain', label: 'Mountain Chalet', emoji: '\u26F0\uFE0F' },
  { id: 'city', label: 'City Apartment', emoji: '\uD83C\uDFD9\uFE0F' },
  { id: 'village', label: 'Village House', emoji: '\uD83C\uDFE1' },
] as const;

interface StepTravelStyleProps {
  selectedStyles: string[];
  onToggleStyle: (style: string) => void;
  onContinue: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const StepTravelStyle: React.FC<StepTravelStyleProps> = ({
  selectedStyles,
  onToggleStyle,
  onContinue,
  onBack,
  onSkip,
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          What kind of stays do you love?
        </h1>
        <p className="text-gray-500 mb-8">
          Select all that interest you. We'll personalize your results.
        </p>

        {/* Travel style cards */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {TRAVEL_STYLES.map((style) => {
            const selected = selectedStyles.includes(style.id);
            return (
              <button
                key={style.id}
                type="button"
                onClick={() => onToggleStyle(style.id)}
                className={`relative flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all duration-200 min-h-[140px] ${
                  selected
                    ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 scale-105'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }`}
              >
                {selected && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <span className="text-4xl mb-3">{style.emoji}</span>
                <span className={`text-sm font-medium ${selected ? 'text-primary-700' : 'text-gray-700'}`}>
                  {style.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={onContinue}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors text-base"
          >
            Continue
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors text-sm"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepTravelStyle;
